import { SimplePagination, SortStatus, Table } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsTableConversationPlaceholderRows } from './components/rows/conversationPlaceholderRows/conversationsTableConversationPlaceholderRows';
import { ConversationsTableConversationRow } from './components/rows/conversationRow/conversationsTableConversationRow';
import { ConversationsTableEmptyRow } from './components/rows/emptyRow/conversationsTableEmptyRow';
import { ConversationsTableHeadRow } from './components/rows/headRow/conversationsTableHeadRow';
import { ConversationsTableRejectedRow } from './components/rows/rejectedRow/conversationsTableRejectedRow';
import styles from './conversationsTable.module.scss';
import {
  type ConversationsTableProps,
  type ConversationsTableSortableField,
  conversationsTableSortableFields,
} from './conversationsTable.types';

/** Represents the conversations table component. */
export const ConversationsTable = ({
  conversations,
  asyncStatus,
  selectableRowsProps,
  paginationProps,
}: ConversationsTableProps): JSX.Element => {
  const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);
  const [isInitialized, setIsInitialized] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const {
    params: { sort },
    setParam,
    unsetParam,
  } = useAppSearchParams('/conversations');
  const [sortStatus, setSortStatus] = useState<SortStatus | null>(null);

  const scrollTableToTop = useCallback(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
    }
  }, []);

  const handleTableSortChange = useCallback(
    (newSortStatus: SortStatus | null) => {
      setSortStatus(newSortStatus);

      if (newSortStatus) {
        setParam('sort', Object.values(newSortStatus).join(':'));
      } else {
        unsetParam('sort');
      }
    },
    [setParam, unsetParam],
  );

  const tableBody = useMemo(() => {
    if (asyncStatus === 'uninitialized' || asyncStatus === 'pending') {
      return <ConversationsTableConversationPlaceholderRows />;
    }

    if (asyncStatus === 'rejected') {
      return <ConversationsTableRejectedRow />;
    }

    if (!conversations.length) {
      return <ConversationsTableEmptyRow />;
    }

    return (
      <>
        {conversations.map((conversation) => (
          <ConversationsTableConversationRow
            key={conversation.id}
            conversation={conversation}
          />
        ))}
      </>
    );
  }, [conversations, asyncStatus]);

  // Handles scrolling to the top of the table when there are new items.
  useEffect(() => {
    if (asyncStatus === 'fulfilled' || asyncStatus === 'rejected') {
      scrollTableToTop();

      // Tells that the state has been through the first cycle.
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [asyncStatus, isInitialized, scrollTableToTop]);

  // Handles settings the sort status by reading the sort search param.
  useEffectOnce(() => {
    if (!sort) {
      // Because the default sorting of conversations and created_at can be
      // considered the same we only set the sorting state on the column and
      // don't do any fetching of data until the user interacts with one of the
      // sorting columns.
      setSortStatus({
        sortBy: 'created_at' as const satisfies ConversationsTableSortableField,
        direction: 'desc',
      });
    } else {
      const sortObject = getSortObjectFromString(
        sort,
        conversationsTableSortableFields,
      );

      if (sortObject && sortObject.direction) {
        setSortStatus({
          sortBy: sortObject.key,
          direction: sortObject.direction,
        });
      }
    }
  });

  return (
    <div className={styles.root}>
      <Table
        stickyHeader
        hoverable={asyncStatus === 'fulfilled' && conversations.length > 0}
        loading={asyncStatus === 'uninitialized' || asyncStatus === 'pending'}
        loadingStrategy="disabled"
        wrapperRef={tableWrapperRef}
        className={classnames(styles.table, {
          [styles.tableExpanded]:
            (asyncStatus === 'fulfilled' && !conversations.length) ||
            asyncStatus === 'rejected',
        })}
        selectableRowsProps={
          conversations.length && hasBulkOperations
            ? selectableRowsProps
            : undefined
        }
        sortingProps={{
          sortStatus,
          onSortChange: handleTableSortChange,
          sortableColumns: [
            'last_message_created_at',
            'created_at',
          ] satisfies ConversationsTableSortableField[],
        }}
      >
        <Table.Thead>
          <ConversationsTableHeadRow isInitialized={isInitialized} />
        </Table.Thead>

        <Table.Tbody>{tableBody}</Table.Tbody>

        <Table.Footer>
          <SimplePagination {...paginationProps} />
        </Table.Footer>
      </Table>
    </div>
  );
};
